import { useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { Layout } from "@/components/Layout"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/Button"
import { Input } from "@/components/Form/Input"
import { WelcomeOfferStatus } from "@/components/WelcomeOfferStatus"
import { SwitchField } from "@/components/Form/Switch"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { useToast } from "@/components/Toast"
import {
  useUpdateWelcomeOfferSettings,
  useWelcomeOfferSettings,
  useWelcomeOffers,
  type WelcomeOfferQueueFilter,
} from "@/hooks/useWelcomeOffers"

const filters: { value: WelcomeOfferQueueFilter; label: string }[] = [
  { value: "accepted", label: "Accepted" },
  { value: "details_received", label: "Details received" },
  { value: "onboarded", label: "Onboarded" },
  { value: "cold", label: "Cold" },
  { value: "all", label: "All requests" },
]

export function WelcomeOffers() {
  const [params, setParams] = useSearchParams()
  const stage =
    filters.find((filter) => filter.value === params.get("stage"))?.value ?? "details_received"
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(0)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(0)
    }, 250)
    return () => clearTimeout(timer)
  }, [searchInput])
  const { data, isPending, isError, refetch } = useWelcomeOffers(stage, search, page)
  const { data: settings } = useWelcomeOfferSettings()
  const updateSettings = useUpdateWelcomeOfferSettings()
  const { toast } = useToast()
  // The switch changes global campaign state, so it confirms first (admin convention: sensitive
  // operations require a confirmation dialog). `pendingEnabled` is the value being confirmed.
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null)
  const rows = data?.requests ?? []
  return (
    <Layout>
      <div className="space-y-6">
        <Link to="/offers" className="inline-block text-sm text-blue-600 hover:underline">
          ← All offers
        </Link>
        <PageHeader
          title="Diagrams welcome offer"
          description="A free year of Playside film for Diagrams coaches."
          actions={
            data && (
              <span className="text-sm text-secondary">
                {data.claimed_count} of {data.cap} spots claimed
              </span>
            )
          }
        />
        {settings && (
          <section className="rounded-xl border border-border bg-surface p-5">
            <SwitchField
              label="Show this offer in Diagrams"
              description="Turn off to stop showing the offer to new Diagrams coaches. Coaches who already accepted keep their request."
              checked={!settings.paused}
              disabled={updateSettings.isPending}
              onCheckedChange={(on) => setPendingEnabled(on)}
            />
            {!settings.env_enabled && (
              <p role="alert" className="mt-3 text-sm text-amber-600">
                This environment has the offer disabled in its deploy config, so it stays hidden
                regardless of this switch.
              </p>
            )}
          </section>
        )}
        <ConfirmDialog
          open={pendingEnabled !== null}
          onOpenChange={(open) => !open && setPendingEnabled(null)}
          title={
            pendingEnabled ? "Show the offer to new coaches?" : "Stop showing the offer?"
          }
          description={
            pendingEnabled
              ? "Newly eligible Diagrams coaches will see the welcome offer again. Coaches who already accepted are unaffected."
              : "The offer stops appearing to newly eligible Diagrams coaches. Coaches who already accepted keep their request, and their answers still record."
          }
          confirmLabel={pendingEnabled ? "Turn the offer on" : "Turn the offer off"}
          confirmVariant={pendingEnabled ? "default" : "destructive"}
          onConfirm={() => {
            const on = pendingEnabled
            setPendingEnabled(null)
            if (on === null) return
            updateSettings.mutate(
              { paused: !on },
              {
                onSuccess: () => toast({ title: on ? "Offer is live" : "Offer paused" }),
                onError: () =>
                  toast({ title: "Could not update the offer", variant: "destructive" }),
              }
            )
          }}
        />
        <div className="flex flex-wrap gap-2" aria-label="Filter requests">
          {filters.map((filter) => (
            <Button
              key={filter.value}
              variant={stage === filter.value ? "default" : "outline"}
              aria-pressed={stage === filter.value}
              onClick={() => {
                setParams({ stage: filter.value })
                setPage(0)
              }}
            >
              {filter.label}
              {data && filter.value !== "all" ? ` (${data.counts[filter.value]})` : ""}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-secondary">
            {stage === "details_received"
              ? "Ready for your review. Oldest submissions first."
              : stage === "accepted"
                ? "Accepted the offer; waiting for team details."
                : "Open a request to review details and follow up."}
          </p>
          <div className="w-full sm:w-80">
            <Input
              aria-label="Search requests"
              placeholder="Search coach, email, or team"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
        </div>
        {isPending ? (
          <p role="status">Loading requests…</p>
        ) : isError ? (
          <div role="alert" className="rounded-lg border border-border p-6">
            <p>Could not load requests.</p>
            <Button className="mt-3" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface p-10 text-center">
            <h2 className="font-semibold">
              {search
                ? "No matching requests"
                : stage === "details_received"
                  ? "No requests waiting for review"
                  : "No requests here yet"}
            </h2>
            <p className="mt-2 text-sm text-secondary">
              {search
                ? "Try another name, email, or team."
                : "New submissions will appear here when coaches send their team details."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-surface">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-muted/40 text-secondary">
                <tr>
                  {["Coach", "Team", "Level", "Status", "Received", ""].map((label, index) => (
                    <th key={index} className="px-5 py-3 font-medium">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((request) => (
                  <tr key={request.public_id} className="hover:bg-muted/30">
                    <td className="px-5 py-4">
                      <Link
                        className="font-medium hover:underline"
                        to={`/offers/diagrams/${request.public_id}`}
                      >
                        {request.name || request.email}
                      </Link>
                      <div className="mt-1 text-xs text-secondary">{request.email}</div>
                    </td>
                    <td className="px-5 py-4">
                      {request.team_name || (
                        <span className="text-secondary">Awaiting details</span>
                      )}
                    </td>
                    <td className="px-5 py-4">{request.level || "—"}</td>
                    <td className="px-5 py-4">
                      <WelcomeOfferStatus stage={request.stage} />
                    </td>
                    <td className="px-5 py-4 text-secondary">
                      {request.submitted_at
                        ? new Date(request.submitted_at).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-5 py-4">
                      <Link
                        className="font-medium text-blue-600 hover:underline"
                        to={`/offers/diagrams/${request.public_id}`}
                      >
                        Review<span className="sr-only"> {request.name || request.email}</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(page > 0 || data?.has_more) && (
          <div className="flex items-center justify-end gap-3">
            <Button variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <span className="text-sm text-secondary">Page {page + 1}</span>
            <Button variant="outline" disabled={!data?.has_more} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        )}
      </div>
    </Layout>
  )
}
